import React, { useEffect, useState } from 'react'
import {
    Box,
    Button,
    CircularProgress,
    FormControlLabel,
    makeStyles,
    MenuItem,
    Switch,
    TextField,
    Theme,
    Tooltip,
    Typography,
} from '@material-ui/core'
import AddIcon from '@material-ui/icons/Add'
import {
    ApartmentContract,
    ApartmentContractType,
    Company,
    ContractPaymentTerm,
} from '../../models/Company/types'
import { updateContractRequest } from '../../hooks/useCompanyContracts'
import { useOrganizationCertificateTypes } from '../../hooks/useOrganizationCertificateTypes'
import { useOrganizationContractTypes } from '../../hooks/useOrganizationContractTypes'
import { useUser } from '../../hooks/useUser'
import { QuickAddVendorModal, QuickVendorData } from './QuickAddVendorModal'

interface ContractDetailsFormProps {
    contract: ApartmentContract
    vendorList: { id: number; name: string }[]
    apartmentList?: Company[]
    onSave: (
        contractId: number,
        data: updateContractRequest,
    ) => void | Promise<unknown>
    isSaving: boolean
    hideVendorField?: boolean
    onCreateVendor?: (data: QuickVendorData) => Promise<number>
    isCreatingVendor?: boolean
    onCancel: () => void
    onSaved: () => void
}

const useStyles = makeStyles((theme: Theme) => ({
    form: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(2),
    },
    actions: {
        display: 'flex',
        justifyContent: 'flex-end',
        gap: theme.spacing(1),
        paddingTop: theme.spacing(1),
    },
}))

const PAYMENT_TERM_OPTIONS: { value: ContractPaymentTerm; label: string }[] = [
    { value: ContractPaymentTerm.MONTHLY, label: 'Monthly' },
    { value: ContractPaymentTerm.QUARTERLY, label: 'Quarterly' },
    { value: ContractPaymentTerm.ANNUALLY, label: 'Annually' },
    { value: ContractPaymentTerm.ONE_TIME, label: 'One-Time' },
    { value: ContractPaymentTerm.PER_JOB, label: 'Per Job' },
]

const CONTRACT_TYPE_OPTIONS = [
    {
        label: 'Certificates',
        value: ApartmentContractType.CERTIFICATE,
    },
    { label: 'Turn Vendors', value: ApartmentContractType.TURN },
    {
        label: 'General Vendor',
        value: ApartmentContractType.GENERAL_VENDOR,
    },
]

const calendarDate = (value: string | null | undefined) =>
    value ? value.split('T')[0] : ''

export const ContractDetailsForm: React.FC<ContractDetailsFormProps> = ({
    contract,
    vendorList,
    apartmentList = [],
    onSave,
    isSaving,
    hideVendorField = false,
    onCreateVendor,
    isCreatingVendor = false,
    onCancel,
    onSaved,
}) => {
    const classes = useStyles()
    const [addVendorModalOpen, setAddVendorModalOpen] = useState(false)
    const [name, setName] = useState(contract.name || '')
    const [vendorId, setVendorId] = useState<number | ''>(
        contract.vendor?.id || '',
    )
    const [apartmentIds, setApartmentIds] = useState<number[]>(
        contract.apartment_ids ?? contract.apartments ?? [],
    )
    const [startDate, setStartDate] = useState(calendarDate(contract.start_date))
    const [endDate, setEndDate] = useState(calendarDate(contract.end_date))
    const [paymentAmount, setPaymentAmount] = useState(
        contract.payment_amount?.toString() || '',
    )
    const [paymentTerm, setPaymentTerm] = useState<ContractPaymentTerm | ''>(
        contract.payment_term || '',
    )
    const [contractType, setContractType] = useState<ApartmentContractType>(
        contract.contract_type ?? ApartmentContractType.GENERAL_VENDOR,
    )
    const [
        organizationContractTypeId,
        setOrganizationContractTypeId,
    ] = useState<number | ''>(
        (contract.contract_type === ApartmentContractType.CERTIFICATE
            ? contract.organization_certificate_type
            : contract.organization_contract_type) ?? '',
    )
    const [autoRenew, setAutoRenew] = useState(contract.auto_renew ?? false)
    const [renewalTermMonths, setRenewalTermMonths] = useState(
        contract.renewal_term_months?.toString() || '',
    )
    const [noticeLeadDays, setNoticeLeadDays] = useState(
        contract.notice_lead_days?.toString() || '',
    )

    const { workspaceUser, organization: userOrganization } = useUser()
    const organizationId =
        workspaceUser?.active_workspace?.organization ??
        userOrganization ??
        null
    const isCertificate = contractType === ApartmentContractType.CERTIFICATE
    const { contractTypes } = useOrganizationContractTypes({
        organization: isCertificate ? null : organizationId,
        includeInactive: false,
    })
    const { certificateTypes } = useOrganizationCertificateTypes({
        organization: isCertificate ? organizationId : null,
        includeInactive: false,
    })
    const catalogTypes = isCertificate ? certificateTypes : contractTypes

    useEffect(() => {
        setName(contract.name || '')
        setVendorId(contract.vendor?.id || '')
        setApartmentIds(contract.apartment_ids ?? contract.apartments ?? [])
        setStartDate(calendarDate(contract.start_date))
        setEndDate(calendarDate(contract.end_date))
        setPaymentAmount(contract.payment_amount?.toString() || '')
        setPaymentTerm(contract.payment_term || '')
        setContractType(
            contract.contract_type ?? ApartmentContractType.GENERAL_VENDOR,
        )
        setOrganizationContractTypeId(
            (contract.contract_type === ApartmentContractType.CERTIFICATE
                ? contract.organization_certificate_type
                : contract.organization_contract_type) ?? '',
        )
        setAutoRenew(contract.auto_renew ?? false)
        setRenewalTermMonths(contract.renewal_term_months?.toString() || '')
        setNoticeLeadDays(contract.notice_lead_days?.toString() || '')
    }, [contract.id])

    const handleSave = async () => {
        const data: updateContractRequest = {
            file_name: name.trim() || contract.name,
            vendor: vendorId ? Number(vendorId) : undefined,
            apartment_ids: apartmentList.length > 0 ? apartmentIds : undefined,
            start_date: startDate || undefined,
            end_date: endDate || undefined,
            payment_amount: paymentAmount ? Number(paymentAmount) : undefined,
            payment_term: paymentTerm || undefined,
            contract_type: contractType,
            organization_contract_type: isCertificate
                ? null
                : organizationContractTypeId === ''
                ? null
                : organizationContractTypeId,
            organization_certificate_type: isCertificate
                ? organizationContractTypeId === ''
                    ? null
                    : organizationContractTypeId
                : null,
            auto_renew: autoRenew,
            renewal_term_months: renewalTermMonths
                ? Number(renewalTermMonths)
                : null,
            notice_lead_days: noticeLeadDays ? Number(noticeLeadDays) : null,
        }

        try {
            await onSave(contract.id, data)
            onSaved()
        } catch {
            // The mutation reports the error.
        }
    }

    return (
        <>
            <Box className={classes.form}>
                <TextField
                    label="Contract Name"
                    variant="outlined"
                    size="small"
                    fullWidth
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    disabled={isSaving}
                />

                <TextField
                    label={isCertificate ? 'Certificate Type' : 'Contract Type'}
                    variant="outlined"
                    size="small"
                    fullWidth
                    select
                    value={organizationContractTypeId}
                    onChange={(e) =>
                        setOrganizationContractTypeId(
                            e.target.value === '' ? '' : Number(e.target.value),
                        )
                    }
                    disabled={isSaving}
                    helperText="Organization catalog. Leave untyped if none apply."
                >
                    <MenuItem value="">Untyped</MenuItem>
                    {catalogTypes.map((type) => (
                        <MenuItem key={type.id} value={type.id}>
                            {type.name}
                        </MenuItem>
                    ))}
                </TextField>

                <TextField
                    label="Category"
                    variant="outlined"
                    size="small"
                    fullWidth
                    select
                    value={contractType}
                    onChange={(e) => {
                        const next = e.target.value as ApartmentContractType
                        setContractType(next)
                        if (
                            (next === ApartmentContractType.CERTIFICATE) !==
                            (contractType === ApartmentContractType.CERTIFICATE)
                        ) {
                            setOrganizationContractTypeId('')
                        }
                    }}
                    disabled={isSaving}
                >
                    {CONTRACT_TYPE_OPTIONS.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                            {option.label}
                        </MenuItem>
                    ))}
                </TextField>

                {!hideVendorField && (
                    <Box display="flex" alignItems="flex-start" style={{ gap: 8 }}>
                        <TextField
                            label="Vendor"
                            variant="outlined"
                            size="small"
                            fullWidth
                            select
                            value={vendorId}
                            onChange={(e) =>
                                setVendorId(
                                    e.target.value ? Number(e.target.value) : '',
                                )
                            }
                            disabled={isSaving}
                        >
                            <MenuItem value="">
                                <em>None</em>
                            </MenuItem>
                            {vendorList.map((vendor) => (
                                <MenuItem key={vendor.id} value={vendor.id}>
                                    {vendor.name}
                                </MenuItem>
                            ))}
                        </TextField>
                        {onCreateVendor && (
                            <Tooltip title="Add New Vendor">
                                <Button
                                    variant="outlined"
                                    color="primary"
                                    onClick={() => setAddVendorModalOpen(true)}
                                    disabled={isSaving}
                                    style={{ minWidth: 40, padding: 8 }}
                                >
                                    <AddIcon />
                                </Button>
                            </Tooltip>
                        )}
                    </Box>
                )}

                {apartmentList.length > 0 && (
                    <TextField
                        label="Properties"
                        variant="outlined"
                        size="small"
                        fullWidth
                        select
                        SelectProps={{ multiple: true }}
                        value={apartmentIds}
                        onChange={(e) => {
                            const value = e.target.value
                            setApartmentIds(
                                typeof value === 'string'
                                    ? []
                                    : (value as number[]),
                            )
                        }}
                        disabled={isSaving}
                    >
                        {apartmentList.map((apt) => (
                            <MenuItem key={apt.id} value={apt.id}>
                                {apt.name}
                            </MenuItem>
                        ))}
                    </TextField>
                )}

                <TextField
                    label="Start Date"
                    variant="outlined"
                    size="small"
                    type="date"
                    fullWidth
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    disabled={isSaving}
                />

                <TextField
                    label="End Date"
                    variant="outlined"
                    size="small"
                    type="date"
                    fullWidth
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    disabled={isSaving}
                />

                <TextField
                    label="Payment Amount"
                    variant="outlined"
                    size="small"
                    type="number"
                    fullWidth
                    value={paymentAmount}
                    onChange={(e) => setPaymentAmount(e.target.value)}
                    InputProps={{
                        startAdornment: (
                            <Typography
                                variant="body2"
                                color="textSecondary"
                                style={{ marginRight: 4 }}
                            >
                                $
                            </Typography>
                        ),
                    }}
                    disabled={isSaving}
                />

                <TextField
                    label="Payment Term"
                    variant="outlined"
                    size="small"
                    fullWidth
                    select
                    value={paymentTerm}
                    onChange={(e) =>
                        setPaymentTerm(e.target.value as ContractPaymentTerm | '')
                    }
                    disabled={isSaving}
                >
                    <MenuItem value="">
                        <em>None</em>
                    </MenuItem>
                    {PAYMENT_TERM_OPTIONS.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                            {option.label}
                        </MenuItem>
                    ))}
                </TextField>

                <FormControlLabel
                    control={
                        <Switch
                            checked={autoRenew}
                            onChange={(e) => setAutoRenew(e.target.checked)}
                            color="primary"
                            disabled={isSaving}
                        />
                    }
                    label="Auto-Renew"
                />

                {autoRenew && (
                    <>
                        <TextField
                            label="Renewal Term (months)"
                            variant="outlined"
                            size="small"
                            type="number"
                            fullWidth
                            value={renewalTermMonths}
                            onChange={(e) => setRenewalTermMonths(e.target.value)}
                            placeholder="12"
                            disabled={isSaving}
                        />
                        <TextField
                            label="Notice Lead Days"
                            variant="outlined"
                            size="small"
                            type="number"
                            fullWidth
                            value={noticeLeadDays}
                            onChange={(e) => setNoticeLeadDays(e.target.value)}
                            placeholder="120"
                            helperText="Days before end date to alert. Default: 120"
                            disabled={isSaving}
                        />
                    </>
                )}

                <Box className={classes.actions}>
                    <Button
                        onClick={onCancel}
                        variant="outlined"
                        color="default"
                        disabled={isSaving}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSave}
                        variant="contained"
                        color="primary"
                        disabled={isSaving}
                        startIcon={
                            isSaving ? (
                                <CircularProgress size={16} color="inherit" />
                            ) : null
                        }
                    >
                        {isSaving ? 'Saving...' : 'Save Changes'}
                    </Button>
                </Box>
            </Box>

            {onCreateVendor && (
                <QuickAddVendorModal
                    isOpen={addVendorModalOpen}
                    onClose={() => setAddVendorModalOpen(false)}
                    onVendorCreated={(newVendorId) => {
                        setVendorId(newVendorId)
                        setAddVendorModalOpen(false)
                    }}
                    createVendor={onCreateVendor}
                    isCreating={isCreatingVendor}
                />
            )}
        </>
    )
}
